// En förfrågan i inkorgen (designen Admin-Offert, ADR 0069): offerten som går
// ut med AI:s motiveringar, kundens svar, AI:s bedömning, inledningen och
// händelserna. En offert som väntar kan godkännas, ändras eller avslås. När
// byrån ändrar tjänsterna räknar prismotorn om offerten i API:t.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { Link, useParams } from 'react-router';
import { ApiFel, data } from '../api/client.js';
import { useAuth } from '../auth/auth.js';
import {
  bolagsformText,
  handelseText,
  kundnamn,
  lageChip,
  lageText,
  svarLista,
  tidpunkt,
  vantar,
  type Detalj,
} from '../inkorg/texter.js';
import { Andra } from '../inkorg/Andra.js';
import { tomtUrval, type Urval } from '../inkorg/urval.js';
import type { ServiceCatalog } from '../katalog/sammanfattning.js';
import type { LegalForm } from '../katalog/texter.js';
import { Sida } from '../layout/Sida.js';
import { beloppText, orsakText, type Berakning } from '../testlage/texter.js';

export function Forfragan() {
  const { id = '' } = useParams();
  const { api } = useAuth();
  const queryClient = useQueryClient();
  const rubrikId = useId();

  const detalj = useQuery({
    queryKey: ['forfragan', id],
    queryFn: async () =>
      data(await api.GET('/v1/admin/inquiries/{id}', { params: { path: { id } } })),
    retry: (antal, fel) => !(fel instanceof ApiFel && fel.status === 404) && antal < 2,
  });

  const d = detalj.data;
  const modellId = d?.quote?.pricingModel.id;
  const katalog = useQuery({
    queryKey: ['prismodell', modellId],
    enabled: Boolean(modellId),
    queryFn: async () =>
      data(
        await api.GET('/v1/admin/pricing-models/{id}', {
          params: { path: { id: modellId ?? '' } },
        }),
      ),
    staleTime: Infinity,
  });

  const [andrar, setAndrar] = useState(false);
  const [urval, setUrval] = useState<Urval | null>(null);
  const [omraknad, setOmraknad] = useState<Berakning | null>(null);
  const [intro, setIntro] = useState<string | null>(null);
  const [redigerarIntro, setRedigerarIntro] = useState(false);
  const [kvitto, setKvitto] = useState<string | null>(null);

  const uppdatera = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['forfragan', id] }),
      queryClient.invalidateQueries({ queryKey: ['inkorg'] }),
      queryClient.invalidateQueries({ queryKey: ['statistik'] }),
    ]);
  };

  const rakna = useMutation({
    mutationFn: async (u: Urval) =>
      data(
        await api.POST('/v1/admin/pricing-models/{id}/simulate', {
          params: { path: { id: modellId ?? '' } },
          body: u,
        }),
      ),
    onSuccess: (b) => setOmraknad(b),
  });

  const skicka = useMutation({
    mutationFn: async () => {
      const offert = d?.quote;
      if (!offert) throw new Error('Offerten saknas');
      const andradIntro = intro !== null && intro.trim() !== (offert.introText ?? '');
      return data(
        await api.POST('/v1/admin/quotes/{id}/send', {
          params: { path: { id: offert.id } },
          body: {
            ...(andrar && urval ? { selection: urval } : {}),
            ...(andradIntro ? { introText: intro.trim() || null } : {}),
          },
        }),
      );
    },
    onSuccess: async () => {
      setAndrar(false);
      setRedigerarIntro(false);
      setOmraknad(null);
      setKvitto('Offerten är godkänd och skickad till kunden.');
      await uppdatera();
    },
  });

  const avsla = useMutation({
    mutationFn: async () => {
      const offert = d?.quote;
      if (!offert) throw new Error('Offerten saknas');
      return data(
        await api.POST('/v1/admin/quotes/{id}/reject', {
          params: { path: { id: offert.id } },
          body: {},
        }),
      );
    },
    onSuccess: async () => {
      setAndrar(false);
      setKvitto('Offerten är avböjd och skickas inte. Kontakta kunden på annat sätt.');
      await uppdatera();
    },
  });

  // Ett fördröjt utskick kan stoppas innan det går iväg.
  const stoppa = useMutation({
    mutationFn: async () => {
      const offert = d?.quote;
      if (!offert) throw new Error('Offerten saknas');
      return data(
        await api.POST('/v1/admin/quotes/{id}/cancel', { params: { path: { id: offert.id } } }),
      );
    },
    onSuccess: async () => {
      setKvitto('Utskicket är stoppat. Offerten skickas inte till kunden.');
      await uppdatera();
    },
  });

  if (detalj.isError) {
    const saknas = detalj.error instanceof ApiFel && detalj.error.status === 404;
    return (
      <Sida titel={saknas ? 'Förfrågan finns inte' : 'Förfrågan'} brodsmulor={<Brodsmulor />}>
        <div className="meddelande" role="alert">
          {saknas
            ? 'Förfrågan finns inte, eller hör till en annan byrå.'
            : 'Förfrågan gick inte att hämta. Ladda om sidan och försök igen.'}
        </div>
      </Sida>
    );
  }
  if (!d) {
    return (
      <Sida titel="Förfrågan" brodsmulor={<Brodsmulor />}>
        <p role="status">Hämtar förfrågan …</p>
      </Sida>
    );
  }

  const { namn } = kundnamn(d.contact);
  const offert = d.quote;
  const kanBesluta = vantar(d.state) && offert !== null;
  const def = katalog.data?.definition as ServiceCatalog | undefined;
  const upptagen = skicka.isPending || avsla.isPending || stoppa.isPending;
  const schemalagd = offert?.status === 'scheduled';

  function borjaAndra() {
    if (!offert) return;
    const form = (d?.inquiry.answers.legalForm as LegalForm | undefined) ?? 'AB';
    setUrval(offert.selection ?? tomtUrval(form));
    setOmraknad(null);
    setKvitto(null);
    setAndrar(true);
  }

  async function visaSomKunden() {
    if (!offert) return;
    // Fönstret öppnas direkt vid klicket, så att webbläsaren inte stoppar det.
    const flik = window.open('', '_blank');
    const svar = await api.GET('/v1/admin/quotes/{id}/preview', {
      params: { path: { id: offert.id } },
      parseAs: 'text',
    });
    if (!svar.data) {
      flik?.close();
      return;
    }
    const url = URL.createObjectURL(new Blob([svar.data as string], { type: 'text/html' }));
    if (flik) flik.location.href = url;
  }

  const berakning: Berakning | null = andrar ? omraknad : (offert?.calculation ?? null);

  return (
    <Sida
      titel={namn}
      brodsmulor={<Brodsmulor namn={namn} />}
      bredvid={
        <span className={`chip ${lageChip(d.state)}`}>{lageText(d.state, d.reviewReasons)}</span>
      }
    >
      <div className="forfragan-huvud">
        <p className="kontaktrad">
          {offert && (
            <>
              Offert <span className="mono">{offert.number}</span>
            </>
          )}
          {d.contact && (
            <>
              {offert && ' · '}
              {d.contact.companyName ? `${d.contact.name} · ` : ''}
              <a href={`mailto:${d.contact.email}`}>{d.contact.email}</a>
              {d.contact.phone ? ` · ${d.contact.phone}` : ''}
              {d.contact.orgNumber ? ` · Org.nr ${orgnummerText(d.contact.orgNumber)}` : ''}
            </>
          )}
        </p>
        {kanBesluta && (
          <div className="knappar">
            <button
              type="button"
              className="btn btn-g farlig"
              disabled={upptagen}
              onClick={() => {
                if (window.confirm('Avböj offerten? Den skickas aldrig till kunden.'))
                  avsla.mutate();
              }}
            >
              Avböj
            </button>
            {!andrar && (
              <button type="button" className="btn btn-s" disabled={upptagen} onClick={borjaAndra}>
                Ändra offerten
              </button>
            )}
            <button
              type="button"
              className="btn btn-p"
              disabled={upptagen || (andrar && !omraknad)}
              onClick={() => skicka.mutate()}
            >
              {skicka.isPending ? 'Skickar …' : 'Godkänn och skicka'}
            </button>
          </div>
        )}
        {schemalagd && (
          <div className="knappar">
            {offert.scheduledSendAt && (
              <span className="hjalp">Skickas {tidpunkt(offert.scheduledSendAt)}</span>
            )}
            <button
              type="button"
              className="btn btn-g farlig"
              disabled={upptagen}
              onClick={() => {
                if (window.confirm('Stoppa utskicket? Offerten skickas då aldrig till kunden.'))
                  stoppa.mutate();
              }}
            >
              {stoppa.isPending ? 'Stoppar …' : 'Stoppa utskicket'}
            </button>
          </div>
        )}
        {!offert && d.contact && (
          <a className="btn btn-p" href={`mailto:${d.contact.email}`}>
            Svara med e-post
          </a>
        )}
      </div>
      <div className="levande" aria-live="polite">
        {kvitto && <div className="kvitto">{kvitto}</div>}
      </div>
      {(skicka.isError || avsla.isError || stoppa.isError) && (
        <div className="meddelande" role="alert">
          {beslutsfel(skicka.error ?? avsla.error ?? stoppa.error)}
        </div>
      )}
      <div className="forfragan">
        <div className="forfragan-huvudkolumn">
          {offert && (
            <section className="kort flikkort" aria-labelledby={`${rubrikId}-offert`}>
              <div className="resultat-status">
                <h2 id={`${rubrikId}-offert`} className="avsnitt-rubrik">
                  {andrar ? 'Ändra offerten' : vantar(d.state) ? 'Offerten som går ut' : 'Offerten'}
                </h2>
                {!andrar && (
                  <button
                    type="button"
                    className="btn btn-g hogerknapp"
                    onClick={() => void visaSomKunden()}
                  >
                    Visa som kunden ser den
                  </button>
                )}
              </div>
              {andrar && urval && def ? (
                <Andra
                  katalog={def}
                  urval={urval}
                  onChange={(u) => {
                    setUrval(u);
                    setOmraknad(null);
                  }}
                  onRakna={() => rakna.mutate(urval)}
                  raknar={rakna.isPending}
                  fel={
                    rakna.isError
                      ? rakna.error instanceof ApiFel && rakna.error.status === 400
                        ? 'Urvalet går inte att räkna. Kontrollera antalen och att varje tjänst har det den behöver.'
                        : 'Det gick inte att räkna om offerten. Försök igen.'
                      : null
                  }
                  onAvbryt={() => {
                    setAndrar(false);
                    setOmraknad(null);
                  }}
                />
              ) : andrar ? (
                <p role="status">Hämtar katalogen …</p>
              ) : null}
              {berakning ? (
                <Rader berakning={berakning} />
              ) : (
                !andrar && (
                  <p className="hjalp">
                    Offerten har inga tjänster än. Välj Ändra offerten och välj tjänsterna.
                  </p>
                )
              )}
            </section>
          )}
          <section className="kort flikkort" aria-labelledby={`${rubrikId}-forfragan`}>
            <h2 id={`${rubrikId}-forfragan`} className="avsnitt-rubrik">
              {d.inquiry.kind === 'question' ? 'Kundens fråga' : 'Kundens förfrågan'}
            </h2>
            {/* Ingen automatisk offert, och varför (ADR 0120). */}
            {d.inquiry.quoteSkippedReason && (
              <p className="kort notis" role="note">
                {d.inquiry.quoteSkippedReason === 'quota'
                  ? 'Ingen automatisk offert: månadens offerter var slut. Svara kunden själv.'
                  : 'Ingen automatisk offert: byrån hade inget aktivt abonnemang. Svara kunden själv.'}{' '}
                <Link to="/abonnemang">Abonnemang</Link>
              </p>
            )}
            <dl className="kv">
              {svarLista(d.inquiry.answers).map((s) => (
                <div key={s.namn}>
                  <dt>{s.namn}</dt>
                  <dd>{s.varde}</dd>
                </div>
              ))}
              <div>
                <dt>{d.inquiry.kind === 'question' ? 'Frågan' : 'Övrigt'}</dt>
                <dd className={d.inquiry.freeText ? 'fritext' : 'hjalp'}>
                  {d.inquiry.freeText ?? 'Inget'}
                </dd>
              </div>
              <div>
                <dt>Mottagen</dt>
                <dd>{tidpunkt(d.inquiry.receivedAt)}</dd>
              </div>
              {d.inquiry.sourceUrl && (
                <div>
                  <dt>Skickad från</dt>
                  <dd className="bryt">{d.inquiry.sourceUrl.replace(/^https?:\/\//, '')}</dd>
                </div>
              )}
            </dl>
          </section>
        </div>
        <aside className="forfragan-sida" aria-label="Bedömning och händelser">
          {offert && <Bedomning d={d} katalog={def} />}
          {offert && (
            <section className="kort flikkort" aria-labelledby={`${rubrikId}-intro`}>
              <h2 id={`${rubrikId}-intro`} className="avsnitt-rubrik">
                Inledning till kunden
              </h2>
              {redigerarIntro ? (
                <>
                  <label className="visuellt-dold" htmlFor={`${rubrikId}-introtext`}>
                    Inledning till kunden
                  </label>
                  <textarea
                    id={`${rubrikId}-introtext`}
                    className="in"
                    rows={6}
                    maxLength={600}
                    value={intro ?? offert.introText ?? ''}
                    onChange={(e) => setIntro(e.target.value)}
                  />
                  <p className="hjalp">Texten sparas när ni godkänner offerten.</p>
                </>
              ) : (
                <p className={(intro ?? offert.introText) ? 'intro' : 'hjalp'}>
                  {(intro ?? offert.introText) || 'Ingen inledning.'}
                </p>
              )}
              {kanBesluta && !redigerarIntro && (
                <button
                  type="button"
                  className="btn btn-g lankknapp"
                  onClick={() => setRedigerarIntro(true)}
                >
                  Redigera texten
                </button>
              )}
            </section>
          )}
          <section className="kort flikkort" aria-labelledby={`${rubrikId}-handelser`}>
            <h2 id={`${rubrikId}-handelser`} className="avsnitt-rubrik">
              Händelser
            </h2>
            <ol className="handelser">
              <li>
                <span className="mono">{tidpunkt(d.inquiry.receivedAt)}</span>
                <span>
                  {d.inquiry.kind === 'question' ? 'Frågan mottagen' : 'Förfrågan mottagen'}
                </span>
              </li>
              {d.events.map((h, i) => (
                <li key={i}>
                  <span className="mono">{tidpunkt(h.occurredAt)}</span>
                  <span>
                    {handelseText(
                      h,
                      h.type === 'created' ? d.interpretation?.latencyMs : undefined,
                    )}
                  </span>
                </li>
              ))}
            </ol>
          </section>
        </aside>
      </div>
    </Sida>
  );
}

/** "5561234567" blir "556123-4567". Ett personnummer med sekel behåller det. */
export const orgnummerText = (nr: string) =>
  /^\d{10}$|^\d{12}$/.test(nr) ? `${nr.slice(0, -4)}-${nr.slice(-4)}` : nr;

function Brodsmulor({ namn }: { namn?: string }) {
  return (
    <nav className="brodsmulor hjalp" aria-label="Brödsmulor">
      <Link to="/">Inkorg</Link>
      {namn && <span aria-current="page"> / {namn}</span>}
    </nav>
  );
}

function beslutsfel(fel: unknown): string {
  if (fel instanceof ApiFel && fel.status === 409)
    return 'Offerten går inte att skicka. Välj minst en tjänst, eller ladda om sidan om någon annan redan har beslutat.';
  if (fel instanceof ApiFel && fel.status === 404)
    return 'Offerten väntar inte längre. Någon annan har redan beslutat om den.';
  if (fel instanceof ApiFel && fel.status === 400)
    return 'Ändringarna går inte att skicka. Kontrollera tjänsterna och antalen.';
  return 'Det gick inte. Försök igen om en stund.';
}

function Bedomning({ d, katalog }: { d: Detalj; katalog: ServiceCatalog | undefined }) {
  const t = d.interpretation;
  const procent = t?.confidencePercent ?? null;
  const justering = d.quote?.calculation.adjustment?.appliedPercent ?? 0;
  const orsaker = d.reviewReasons.filter((o) => o.code !== 'approvalRequired');
  return (
    <section className="kort flikkort bedomning" aria-label="AI:s bedömning">
      <h2 className="avsnitt-rubrik">AI:s bedömning</h2>
      {procent !== null ? (
        <div className="matare">
          <meter min={0} max={100} value={procent} aria-label="AI:s säkerhet" />
          <span className="mono">{procent} %</span>
        </div>
      ) : (
        <p className="hjalp">AI gav inget svar, så tjänsterna behöver väljas av er.</p>
      )}
      {d.state === 'approve' && (
        <p className="hjalp">Säker nog för automatiskt utskick. Ni har valt Godkänn först.</p>
      )}
      {orsaker.length > 0 && (
        <ul className="orsaker">
          {orsaker.map((o, i) => (
            <li key={i}>{orsakText(o, katalog)}</li>
          ))}
        </ul>
      )}
      <dl className="bedomning-lista">
        <div>
          <dt>Flaggor</dt>
          <dd>{t && t.flags.length > 0 ? t.flags.map((f) => f.code).join(', ') : 'Inga'}</dd>
        </div>
        <div>
          <dt>Prisjustering</dt>
          <dd>{justering.toLocaleString('sv-SE')} %</dd>
        </div>
        <div>
          <dt>Bolagsform</dt>
          <dd>{bolagsformText(d.inquiry.answers.legalForm) ?? 'Saknas'}</dd>
        </div>
        {d.quote && (
          <div>
            <dt>Katalog</dt>
            <dd>Version {d.quote.pricingModel.version}</dd>
          </div>
        )}
      </dl>
      {t && t.assumptions.length > 0 && (
        <div>
          <h3 className="liten-rubrik">AI har utgått från</h3>
          <ul className="orsaker">
            {t.assumptions.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function Rader({ berakning }: { berakning: Berakning }) {
  const period = (b: string) => berakning.periods.find((p) => p.billing === b);
  const rader = berakning.periods.flatMap((p) => p.lines);
  return (
    <>
      <div className="tabellrulle" tabIndex={0} role="region" aria-label="Tjänsterna i offerten">
        <table className="tabell offertrader">
          <caption className="visuellt-dold">Tjänsterna i offerten</caption>
          <thead>
            <tr>
              <th scope="col">Tjänst</th>
              <th scope="col">Varför AI valde den</th>
              <th scope="col" className="hoger">
                Pris exkl. moms
              </th>
            </tr>
          </thead>
          <tbody>
            {rader.map((l) => (
              <tr key={l.serviceId}>
                <th scope="row">
                  {l.label}
                  {l.units ? ` × ${l.units.quantity}` : ''}
                </th>
                <td className="hjalp">{l.reason}</td>
                <td className="hoger mono">{beloppText(l.amount, l.billing)}</td>
              </tr>
            ))}
            {berakning.optionalLines.map((l) => (
              <tr key={`tillval-${l.serviceId}`} className="tillval">
                <th scope="row">Tillval: {l.label}</th>
                <td className="hjalp">{l.reason}</td>
                <td className="hoger mono">{beloppText(l.amount, l.billing)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <dl className="summor">
        {(['monthly', 'yearly', 'once'] as const).map((b) => {
          const p = period(b);
          return (
            <div key={b}>
              <dt className="hjalp">
                {{ monthly: 'Per månad', yearly: 'Per år', once: 'En gång' }[b]}
              </dt>
              <dd className="mono">{p ? beloppText(p.total) : '–'}</dd>
            </div>
          );
        })}
      </dl>
    </>
  );
}
